<!--
  Pre-Task Safety Plan Form
  
  Step-by-step safety planning with:
  - 5 steps: Job Details & Task Description, Job Steps & Hazard Analysis, Required Permits & Hazard Assessment, Hazard Conditions & Control Methods, Crew Member Information & Signatures
  - Comprehensive job analysis with dynamic hazard identification and control methods
  - Dynamic hazard control management (up to 7 hazard-control pairs)
  - Checkbox groups for permits, hazard conditions, and control methods
  - Dynamic crew member management (up to 3 additional members)
  - Conditional validation for hazard control fields based on hazard field values
  - Form validation for safety compliance and planning requirements
  - Signature requirements for foreperson (crew members are optional)
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <!-- Job Details & Task Description Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('subcontractor')!"
                [label]="fieldMappings['subcontractor']"
                [requiredField]="requiredFields['subcontractor']"
                placeholder="Enter subcontractor name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('date')!"
                [label]="fieldMappings['date']"
                [requiredField]="requiredFields['date']"
                placeholder="Select date"
                [today]="true"
               
              >
              </app-date-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('jobName')!"
                [label]="fieldMappings['jobName']"
                [requiredField]="requiredFields['jobName']"
                placeholder="Enter job name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('jobNumber')!"
                [label]="fieldMappings['jobNumber']"
                [requiredField]="requiredFields['jobNumber']"
                placeholder="Enter job number"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('taskDescription')!"
                [label]="fieldMappings['taskDescription']"
                [requiredField]="requiredFields['taskDescription']"
                [rows]="4"
                placeholder="Describe the task to be performed"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

   <!-- Job Steps & Hazard Analysis Step -->
   <div *ngIf="currentStep() === steps[1].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
        <ion-label>
          <h1>{{ steps[1].title }}</h1>
          <p>{{ steps[1].description }}</p>
        </ion-label>
      </ion-item-divider>

      <ion-grid>
       

        <ion-row>
          <ion-col>
            <app-textarea-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('jobSteps')!"
              [label]="fieldMappings['jobSteps']"
              [requiredField]="requiredFields['jobSteps']"
              placeholder="Enter all job steps"
              [rows]="4"
            >
            </app-textarea-input>
          </ion-col>
        </ion-row>
       

        <ion-row>
          <ion-col>
          </ion-col>
        </ion-row>
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[1]?.title }}</h2>
            <p>{{ steps[1].sections?.[1]?.description }}</p>
          </ion-label>
        </ion-item-divider>


        <!-- Hazard 1 -->
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[1]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[1]"
        [class.ion-no-margin]="!showHazardControlIndex[1]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard1')!"
              [label]="fieldMappings['identifiedHazard1']"
              [requiredField]="requiredFields['identifiedHazard1']"
              placeholder="Enter first identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl1')!"
              [label]="fieldMappings['hazardControl1']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard1')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        <!-- Hazard 2 -->
        <ng-container *ngIf="showHazardControlIndex[1]">
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[2]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[2]"
        [class.ion-no-margin]="!showHazardControlIndex[2]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard2')!"
              [label]="fieldMappings['identifiedHazard2']"
              placeholder="Enter second identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl2')!"
              [label]="fieldMappings['hazardControl2']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard2')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        </ng-container>

        <!-- Hazard 3 -->
        <ng-container *ngIf="showHazardControlIndex[2]">
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[3]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[3]"
        [class.ion-no-margin]="!showHazardControlIndex[3]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard3')!"
              [label]="fieldMappings['identifiedHazard3']"
              placeholder="Enter third identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl3')!"
              [label]="fieldMappings['hazardControl3']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard3')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        </ng-container>

        <!-- Hazard 4 -->
        <ng-container *ngIf="showHazardControlIndex[3]">
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[4]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[4]"
        [class.ion-no-margin]="!showHazardControlIndex[4]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard4')!"
              [label]="fieldMappings['identifiedHazard4']"
              placeholder="Enter fourth identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl4')!"
              [label]="fieldMappings['hazardControl4']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard4')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        </ng-container>

        <!-- Hazard 5 -->
        <ng-container *ngIf="showHazardControlIndex[4]">
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[5]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[5]"
        [class.ion-no-margin]="!showHazardControlIndex[5]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard5')!"
              [label]="fieldMappings['identifiedHazard5']"
              placeholder="Enter fifth identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl5')!"
              [label]="fieldMappings['hazardControl5']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard5')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        </ng-container>

        <!-- Hazard 6 -->
        <ng-container *ngIf="showHazardControlIndex[5]">
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[1].sections?.[6]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[6]"
        [class.ion-no-margin]="!showHazardControlIndex[6]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard6')!"
              [label]="fieldMappings['identifiedHazard6']"
              placeholder="Enter sixth identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl6')!"
              [label]="fieldMappings['hazardControl6']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard6')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        </ng-container>

        <!-- Hazard 7 -->
        <ng-container *ngIf="showHazardControlIndex[6]">
        <ion-item-divider class="form-subsection-title"  >
          <ion-label>
            <h2>{{ steps[1].sections?.[7]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>
        <ion-row [class.ion-no-padding]="!showHazardControlIndex[7]"
        [class.ion-no-margin]="!showHazardControlIndex[7]">
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('identifiedHazard7')!"
              [label]="fieldMappings['identifiedHazard7']"
              placeholder="Enter seventh identified hazard"
            >
            </app-text-input>
          </ion-col>
          <ion-col size="12" size-md="6">
            <app-text-input
              [templateFormGroup]="jobAnalysisFormGroup"
              [control]="jobAnalysisFormGroup.get('hazardControl7')!"
              [label]="fieldMappings['hazardControl7']"
              [requiredField]="jobAnalysisFormGroup.get('identifiedHazard7')!.value !== ''"
              placeholder="Enter the control method for the identified hazard"
            >
            </app-text-input>
          </ion-col>
        </ion-row>
        </ng-container>

        <!-- Add Hazard Control Button -->
        <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
          <ion-button (click)="addHazardControl()" fill="clear"
          [title]="showAddHazardControlButton ? 'Add Hazard Control' : ''"
          [disabled]="!showAddHazardControlButton"
        >
          <ion-icon
            name="add-circle-outline"
            slot="icon-only"
          ></ion-icon>
        </ion-button>
        <ion-text *ngIf="!showAddHazardControlButton" class="add-other-button-text"
          >Maximum Reached</ion-text
        >
    
      </ion-row>

      </ion-grid>
    </ion-list>
  </div>

    <!-- Required Permits & Hazard Assessment Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="requiredPermitsFormGroup"
                [checkboxes]="requiredPermitsCheckboxes"
                [label]="fieldMappings['requiredPermits']"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>

          <ion-row *ngIf="requiredPermitsFormGroup.get('otherPermit')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="permitsHazardsFormGroup"
                [control]="permitsHazardsFormGroup.get('otherPermitDescription')!"
                [label]="fieldMappings['otherPermitDescription']"
                placeholder="Describe other permit requirements"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="permitsHazardsFormGroup"
                [control]="permitsHazardsFormGroup.get('fireExplosionToxicHazard')!"
                [label]="fieldMappings['fireExplosionToxicHazard']"
                [requiredField]="requiredFields['fireExplosionToxicHazard']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="permitsHazardsFormGroup"
                [control]="permitsHazardsFormGroup.get('sdsReviewRequired')!"
                [label]="fieldMappings['sdsReviewRequired']"
                [requiredField]="requiredFields['sdsReviewRequired']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Hazard Conditions & Control Methods Step -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="hazardConditionsFormGroup"
                [checkboxes]="hazardConditionsCheckboxes"
                [label]="fieldMappings['hazardConditions']"
                [columns]="2"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>

          <ion-row *ngIf="hazardConditionsFormGroup.get('otherHazardConditions')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="hazardEvaluationFormGroup"
                [control]="hazardEvaluationFormGroup.get('otherConditions')!"
                [label]="fieldMappings['otherConditions']"
                placeholder="Describe other conditions"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="controlMethodsFormGroup"
                [checkboxes]="controlMethodsCheckboxes"
                [label]="fieldMappings['controlMethods']"
                [columns]="2"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>

          <ion-row *ngIf="controlMethodsFormGroup.get('otherControlMethods')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="hazardEvaluationFormGroup"
                [control]="hazardEvaluationFormGroup.get('otherControlMethod')!"
                [label]="fieldMappings['otherControlMethod']"
                placeholder="Describe other control methods"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Crew Member Information & Signatures Step -->
    <div *ngIf="currentStep() === steps[4].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[4].title }}</h1>
            <p>{{ steps[4].description }}</p>
          </ion-label>
        </ion-item-divider>

        <!-- Foreperson -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[4].sections?.[0]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('forepersonFirstName')!"
                [label]="fieldMappings['forepersonFirstName']"
                [requiredField]="requiredFields['forepersonFirstName']"
                placeholder="Enter first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('forepersonLastName')!"
                [label]="fieldMappings['forepersonLastName']"
                [requiredField]="requiredFields['forepersonLastName']"
                placeholder="Enter last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-signature-section
                [signatureInfo]="formAttachmentRequirements!.signatures![0]!"
              ></app-signature-section>
            </ion-col>
          </ion-row>
        </ion-grid>

      

        <ion-grid>
            <!-- Crew Member 1 -->
            
        <ng-container *ngIf="showCrewMemberIndex[0]">
       
          <ion-item-divider class="form-subsection-title">
            <ion-label>
              <h2>{{ steps[4].sections?.[1]?.subtitle }}</h2>
            </ion-label>
          </ion-item-divider>
    
          <ion-row >
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('crewMember1FirstName')!"
                [label]="fieldMappings['crewMember1FirstName']"
                [requiredField]="requiredFields['crewMember1FirstName']"
                placeholder="Enter first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('crewMember1LastName')!"
                [label]="fieldMappings['crewMember1LastName']"
                [requiredField]="requiredFields['crewMember1LastName']"
                placeholder="Enter last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row
          [class.ion-no-padding]="!showCrewMemberIndex[1]"
          [class.ion-no-margin]="!showCrewMemberIndex[1]">
            <ion-col>
              <app-signature-section
                [signatureInfo]="formAttachmentRequirements!.signatures![1]!"
              ></app-signature-section>
            </ion-col>
          </ion-row>
        </ng-container>

      
<ng-container *ngIf="showCrewMemberIndex[1]">
        <!-- Crew Member 2 -->
        <ion-item-divider class="form-subsection-title">
          <ion-label>
            <h2>{{ steps[4].sections?.[2]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>

    
          <ion-row >
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('crewMember2FirstName')!"
                [label]="fieldMappings['crewMember2FirstName']"
                placeholder="Enter first name (optional)"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('crewMember2LastName')!"
                [label]="fieldMappings['crewMember2LastName']"
                placeholder="Enter last name (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row
          [class.ion-no-padding]="!showCrewMemberIndex[2]"
          [class.ion-no-margin]="!showCrewMemberIndex[2]">
            <ion-col>
              <app-signature-section
                [signatureInfo]="formAttachmentRequirements!.signatures![2]!"
              ></app-signature-section>
            </ion-col>
          </ion-row>
        </ng-container>

        <!-- Crew Member 3 -->
        <ng-container *ngIf="showCrewMemberIndex[2]">
        <ion-item-divider class="form-subsection-title"  >
          <ion-label>
            <h2>{{ steps[4].sections?.[3]?.subtitle }}</h2>
          </ion-label>
        </ion-item-divider>

          <ion-row
          class="ion-no-padding ion-no-margin">
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('crewMember3FirstName')!"
                [label]="fieldMappings['crewMember3FirstName']"
                placeholder="Enter first name (optional)"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('crewMember3LastName')!"
                [label]="fieldMappings['crewMember3LastName']"
                placeholder="Enter last name (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row
          class="ion-no-padding ion-no-margin">
            <ion-col class="ion-no-padding ion-no-margin">
              <app-signature-section
                [signatureInfo]="formAttachmentRequirements!.signatures![3]!"
              ></app-signature-section>
            </ion-col>
          </ion-row>
          </ng-container>
          <!-- Add Crew Member Button -->
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
            <ion-button (click)="addCrewMember()" fill="clear"
            [title]="showAddCrewMemberButton ? 'Add Crew Member' : ''"
            [disabled]="!showAddCrewMemberButton"
            >
              <ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddCrewMemberButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
            <ion-text *ngIf="showAddCrewMemberButton" color="primary"
              >Add Crew Member</ion-text
            >
          </ion-row>
          <!-- Other Notes -->
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="crewSignaturesFormGroup"
                [control]="crewSignaturesFormGroup.get('otherNotes')!"
                [label]="fieldMappings['otherNotes']"
                placeholder="Enter other notes (optional)"
              >
              </app-text-input>
            </ion-col>
            </ion-row>
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout>